import React, { useState } from 'react';
import ContentMetadata from './ContentMetadata';
import ContentMedia from './ContentMedia';
import ContentUpdate from './ContentUpdate';
import { useNavigate } from 'react-router-dom';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import { useEducationHub } from '../../../contexts/EducationHubContext';
import { Guide, NewsPost, UpdateNote, GuideCompletion } from '../../../types/EducationHub';
import styles from '../../../styles/EducationHub.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import LoadingSpinner from 'components/common/LoadingSpinner';

interface ContentViewerProps {
  content: Guide | NewsPost | UpdateNote | null;
  contentType: 'guide' | 'news' | 'update';
  loading: boolean;
  error: string | null;
  userDetails: any;
  onBack?: () => void;
}

const ContentViewer: React.FC<ContentViewerProps> = ({
  content,
  contentType,
  loading,
  error,
  userDetails,
  onBack
}) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useEnhancedAuth();
  const { completions, updateCompletion } = useEducationHub();
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionSuccess, setCompletionSuccess] = useState(false);

  // Get completion status for guides
  const getCompletion = (): GuideCompletion | undefined => {
    if (contentType !== 'guide' || !content) return undefined;
    return completions.find(c => c.guideId && (typeof c.guideId === 'object' ? c.guideId._id === content._id : c.guideId === content._id));
  };

  const completion = getCompletion();

  // Handle guide completion
  const handleToggleCompletion = async () => {
    if (contentType !== 'guide' || !content || !isLoggedIn || !userDetails?.id) return;

    setIsCompleting(true);
    try {
      const isCurrentlyCompleted = completion?.completed || false;
      const newCompletionState = !isCurrentlyCompleted;

      // Use updateCompletion for both creating and updating
      await updateCompletion(content._id, {
        completed: newCompletionState,
        progress: newCompletionState ? 100 : (completion?.progress || 0),
        timeSpent: completion?.timeSpent || 0,
        notes: completion?.notes || '',
        rating: completion?.rating,
        feedback: completion?.feedback || ''
      });

      setCompletionSuccess(true);
      setTimeout(() => setCompletionSuccess(false), 3000);
    } catch (err) {
      console.error('Error toggling completion:', err);
    } finally {
      setIsCompleting(false);
    }
  };

  const getContentIcon = () => {
    switch (contentType) {
      case 'guide': return '📖';
      case 'news': return '📰';
      case 'update': return '🔄';
      default: return '📄';
    }
  };

  const renderBackButton = () => (
    <button
      className={`${buttonStyles.button} ${buttonStyles.secondary} ${styles.backButton}`}
      onClick={() => {
        if (onBack) {
          onBack();
        } else {
          navigate('/education');
        }
      }}
    >
      ← Back to Education Hub
    </button>
  );

  const renderCompletionButton = () => {
    if (contentType !== 'guide' || !isLoggedIn || !userDetails?.id) return null;

    const isCompleted = completion?.completed || false;

    if (isCompleted) return;

    return (
      <>
        <button
          className={`${buttonStyles.button} ${isCompleted ? buttonStyles.primary : buttonStyles.secondary} ${styles.completionButton}`}
          onClick={handleToggleCompletion}
          disabled={isCompleting}
        >
          {isCompleting ? (
            <>
              <div className={styles.miniSpinner} />
              {isCompleted ? 'Unmarking...' : 'Marking...'}
            </>
          ) : (
            <>
              {'Mark as Complete'}
            </>
          )}
        </button>
        {completionSuccess && (
          <div className={styles.successMessage}>
            ✅ Guide {completion?.completed ? 'marked as complete' : 'unmarked'}!
          </div>
        )}
        {completion && completion.progress > 0 && (
          <div className={styles.progressInfo}>
            Progress: {completion.progress}%
            {completion.timeSpent > 0 && ` • Time spent: ${completion.timeSpent} min`}
          </div>
        )}
      </>
    );
  };




  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  if (error) {
    return (
      <div className={styles.viewerContainer}>
        <div className={styles.viewerContent}>
          <div className={styles.backButtonContainer}>
            {renderBackButton()}
          </div>
          <div className={styles.error}>
            <div className={styles.errorText}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className={styles.viewerContainer}>
        <div className={styles.viewerContent}>
          <div className={styles.backButtonContainer}>
            {renderBackButton()}
          </div>
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>{getContentIcon()}</div>
            <h3 className={styles.emptyTitle}>Content Not Found</h3>
            <p className={styles.emptyDescription}>
              The requested content could not be found or may have been removed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.viewerContainer}>
      <div className={styles.viewerContent}>
        <div className={styles.topBar}>
          <div className={styles.backButtonContainer}>
            {renderBackButton()}
          </div>
          {renderCompletionButton()}
        </div>
        <article className={styles.contentViewer}>
          <header className={styles.contentHeader}>
            <h1 className={styles.contentTitle}>
              {getContentIcon()} {content.title}
            </h1>
            {contentType === 'guide' && completion && completion.completed && (
              <div className={styles.completedBadge}>
                ✅ Completed
              </div>
            )}
            {contentType === 'news' && 'featured' in content && content.featured && (
              <div className={styles.featuredBadge}>
                ⭐ Featured
              </div>
            )}
          </header>
          {content && (
            <ContentMetadata content={content} contentType={contentType} />
          )}
          <div className={`${styles.contentBody} ${styles.contentBodyLeft}`}>
            {'description' in content && content.description && (
              <div>
                <p>{content.description}</p>
              </div>
            )}
            {'excerpt' in content && content.excerpt && (
              <div>
                <p>{content.excerpt}</p>
              </div>
            )}
            {content && <ContentMedia content={content} />}
            {contentType === 'update' && content && (
              <ContentUpdate update={content as UpdateNote} />
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default ContentViewer;
